import { len2 } from "../core/math.js";
import {
  CROSS_FINISH,
  FIELD,
  goalMouth,
  type CrossFinishState,
  type Player,
  type Possession,
  type Vec2,
} from "@golazo/shared";
import type { KickContext } from "./kick.js";

/** ¿El jugador esta dentro del area rival? */
export function isInOpponentBox(player: Player): boolean {
  const boxMinX = player.side === "home" ? FIELD.WIDTH - FIELD.BOX_WIDTH : 0;
  const boxMaxX = player.side === "home" ? FIELD.WIDTH : FIELD.BOX_WIDTH;
  const boxTop = FIELD.HEIGHT / 2 - FIELD.BOX_HEIGHT / 2;
  const boxBottom = FIELD.HEIGHT / 2 + FIELD.BOX_HEIGHT / 2;

  return (
    player.pos.x >= boxMinX - player.radius &&
    player.pos.x <= boxMaxX + player.radius &&
    player.pos.y >= boxTop - player.radius &&
    player.pos.y <= boxBottom + player.radius
  );
}

function nearestOpponentDist(player: Player, opponents: readonly Player[]): number {
  let best = Infinity;
  for (const rival of opponents) {
    const d = len2(rival.pos.x - player.pos.x, rival.pos.y - player.pos.y);
    if (d < best) best = d;
  }
  return best;
}

function isPressured(player: Player, opponents: readonly Player[]): boolean {
  return nearestOpponentDist(player, opponents) <= CROSS_FINISH.PRESSURE_RADIUS;
}

/** Activa la ventana de remate tras controlar un centro en el area rival. */
export function onCrossReception(
  catcher: Player,
  possession: Possession,
  opponents: readonly Player[],
): void {
  if (
    possession.pendingKickKind !== "cross" ||
    possession.pendingKickSide !== catcher.side ||
    !isInOpponentBox(catcher) ||
    !isPressured(catcher, opponents)
  ) {
    possession.pendingKickKind = null;
    possession.pendingKickSide = null;
    return;
  }

  possession.crossFinish = {
    playerId: catcher.id,
    timeLeft: CROSS_FINISH.WINDOW,
    window: CROSS_FINISH.WINDOW,
  };

  possession.pendingKickKind = null;
  possession.pendingKickSide = null;
}

/** Avanza la ventana y la cancela si cambia el dueno. */
export function tickCrossFinish(
  possession: Possession,
  owner: Player | null,
  dt: number,
): void {
  const state = possession.crossFinish;
  if (!state) return;

  state.timeLeft -= dt;
  if (state.timeLeft <= 0 || owner?.id !== state.playerId) {
    possession.crossFinish = null;
  }
}

/** ¿Estamos en el punto dulce del remate de centro? */
export function inCrossFinishMoment(possession: Possession, player: Player): boolean {
  const state = possession.crossFinish;
  if (!state || state.playerId !== player.id) return false;

  const elapsed = state.window - state.timeLeft;
  return elapsed >= CROSS_FINISH.SWEET_START && elapsed <= CROSS_FINISH.SWEET_END;
}

/** Permite disparar durante el primer toque si hay ventana de remate activa. */
export function allowsTrapShoot(possession: Possession, player: Player): boolean {
  const state = possession.crossFinish;
  return state?.playerId === player.id && state.timeLeft > 0 && inCrossFinishMoment(possession, player);
}

/** Direccion hacia el palo mas cercano al rematador. */
export function postShotDirection(player: Player): Vec2 {
  const mouth = goalMouth();
  const goalX = player.side === "home" ? FIELD.WIDTH : 0;
  const centerY = FIELD.HEIGHT / 2;
  const postY =
    player.pos.y < centerY
      ? mouth.top + CROSS_FINISH.POST_INSET
      : mouth.bottom - CROSS_FINISH.POST_INSET;

  const dx = goalX - player.pos.x;
  const dy = postY - player.pos.y;
  const len = len2(dx, dy) || 1;
  return { x: dx / len, y: dy / len };
}

/**
 * Si el remate cumple timing + presion + area, clava el balon al palo.
 * Devuelve `true` si se ha aplicado el remate imparable.
 */
export function tryApplyCrossFinishShot(
  player: Player,
  possession: Possession,
  opponents: readonly Player[],
  ctx: KickContext,
): boolean {
  if (ctx.kind !== "shoot") return false;
  if (!isInOpponentBox(player)) return false;
  if (!inCrossFinishMoment(possession, player)) return false;
  if (!isPressured(player, opponents)) return false;

  const dir = postShotDirection(player);
  ctx.dir.x = dir.x;
  ctx.dir.y = dir.y;
  ctx.power = Math.max(ctx.power, CROSS_FINISH.MIN_POWER);
  ctx.directionLocked = true;
  ctx.aiming = true;
  ctx.unstoppable = true;
  possession.crossFinish = null;
  return true;
}

export type { CrossFinishState };
